// Settings: profile (picture, display name), access, preferences (theme, start page, default
// index, position-size risk), privacy (what stays in this browser, account deletion), sign out.
import { useEffect, useState } from 'react';
import { CheckIcon, CircleCheckIcon, FlaskConicalIcon, LogOutIcon, MonitorIcon, MoonIcon, ShieldCheckIcon, SunIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { BROKERS, NSE_INDICES } from '@planb/core';
import { api } from '@/lib/api';
import { useSearchParams } from '@/lib/nav';
import { useIndexStrip, useMarket } from '@/lib/client/use-market';
import { displayName, requestAccess, setTheme, updateMe, useMe, type Prefs, type Theme } from '@/lib/client/use-me';
import { cn } from '@/lib/utils';
import { ReleaseSwitches } from './release-switches';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AppHeader, Page } from './app-header';
import { carry, dateSeed, indexById, signOut } from './common';
import { Pills } from './market-ui';
import { AVATARS, AvatarIcon, UserAvatar } from './user-avatar';

const HOMES = [{ id: '/today', label: 'Today' }, { id: '/', label: 'Market Flow' }, { id: '/movers', label: 'Movers' }, { id: '/journal', label: 'Journal' }];
const RISKS = ['0.5', '1', '2'];
const LOCAL_KEYS = ['mhq.capital', 'mhq.riskPct', 'mhq.chartLayers', 'mhq.sidebar'];

function Row({ title, note, children }: { title: string; note?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="grid gap-4 border-b py-7 last:border-b-0 md:grid-cols-[240px_1fr] md:gap-10">
      <div>
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {note && <p className="mt-1 text-[13px] leading-snug text-muted-foreground">{note}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

function Pref({ label, note, children }: { label: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b py-3.5 first:pt-0 last:border-b-0 last:pb-0">
      <div>
        <div className="text-[14px] font-medium text-ink">{label}</div>
        {note && <div className="text-[12.5px] text-muted-foreground">{note}</div>}
      </div>
      {children}
    </div>
  );
}

export function SettingsApp() {
  const params = useSearchParams();
  const seed = Number(params.get('seed')) || dateSeed();
  const index = indexById(params.get('index'))?.id ?? 'FNO';
  const { session, status, mode, live, version, user, role } = useMarket({ index: 'NIFTY50', seed });
  const strip = useIndexStrip(session, live, version);
  const query = carry({ index, live, seed });
  const me = useMe();
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [lossLimit, setLossLimit] = useState('');
  useEffect(() => { document.title = 'Settings · MoneyHQ'; }, []);
  useEffect(() => { if (me) setName(me.displayName || me.name || ''); }, [me?.displayName, me?.name]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLossLimit(me?.prefs.dayLossLimit ? String(me.prefs.dayLossLimit) : ''); }, [me?.prefs.dayLossLimit]);

  const save = async (patch: Parameters<typeof updateMe>[0], ok = 'Saved') => {
    const err = await updateMe(patch);
    if (err) toast.error(err); else toast.success(ok);
  };
  const prefs: Prefs = me?.prefs ?? {};
  const setPref = (p: Prefs) => save({ prefs: { ...prefs, ...p } });
  const since = me?.createdAt ? new Date(me.createdAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '';
  const theme: Theme = prefs.theme ?? 'light';

  return (
    <>
      <AppHeader section="settings" mode={mode} status={status} user={user} role={role} query={query} strip={strip} />
      <Page className="max-w-[980px]">
        <h1 className="text-[26px] font-semibold">Settings</h1>
        {!me ? <p className="py-16 text-center text-muted-foreground">Loading…</p> : (
          <Card className="mt-5 gap-0 px-6 py-0 md:px-8">
            <div className="flex items-center gap-4 border-b py-7">
              <UserAvatar me={me} size="lg" />
              <div className="min-w-0">
                <div className="font-heading text-[22px] font-semibold text-ink">{displayName(me)}</div>
                <div className="truncate text-sm text-muted-foreground">{me.user}</div>
                {since && <div className="text-[12.5px] text-muted-foreground">Member since {since}</div>}
              </div>
            </div>

            <Row title="Access" note="What your account can see.">
              {me.access ? (
                <div className="flex items-start gap-3 rounded-xl border bg-bull-soft/50 p-4">
                  <ShieldCheckIcon className="mt-0.5 size-5 text-bull-strong" />
                  <div>
                    <div className="font-semibold text-ink">{me.role === 'admin' ? 'Admin' : 'Live board'}</div>
                    <p className="text-[13px] text-muted-foreground">{me.role === 'admin' ? 'You manage the data feed and members.' : 'Your access is approved: the board, movers and stock pages show the live market.'}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-start gap-3 rounded-xl border bg-brand-soft/50 p-4">
                  <FlaskConicalIcon className="mt-0.5 size-5 text-brand-strong" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-ink">Practice mode</div>
                    <p className="text-[13px] text-muted-foreground">Every tool works on a realistic simulated market. The live board opens once your access is approved.</p>
                  </div>
                  {me.accessRequestedAt
                    ? <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-bull-strong"><CircleCheckIcon className="size-4" />Requested</span>
                    : <Button className="rounded-full" onClick={async () => { const ok = await requestAccess(); toast[ok ? 'success' : 'error'](ok ? 'Request sent' : 'Could not send the request'); }}>Request access</Button>}
                </div>
              )}
            </Row>

            <Row title="Profile" note="Your picture and name inside MoneyHQ.">
              <div className="text-[13px] font-medium text-ink">Picture</div>
              <div className="mt-2.5 flex flex-wrap gap-2.5" role="radiogroup" aria-label="Profile picture">
                {me.picture && (
                  <button type="button" role="radio" aria-checked={me.avatar === 'google' || !me.avatar} aria-label="Google photo"
                    onClick={() => save({ avatar: 'google' }, 'Picture updated')}
                    className={cn('rounded-full p-0.5 ring-2 ring-transparent transition', (me.avatar === 'google' || !me.avatar) && 'ring-primary')}>
                    <img src={me.picture} alt="" referrerPolicy="no-referrer" className="size-10 rounded-full object-cover" />
                  </button>
                )}
                {AVATARS.map((a) => (
                  <button key={a.id} type="button" role="radio" aria-checked={me.avatar === a.id} aria-label={a.id}
                    onClick={() => save({ avatar: a.id }, 'Picture updated')}
                    className={cn('relative rounded-full p-0.5 ring-2 ring-transparent transition hover:scale-105', me.avatar === a.id && 'ring-primary')}>
                    <AvatarIcon id={a.id} className="size-10" />
                    {me.avatar === a.id && <CheckIcon className="absolute -right-0.5 -bottom-0.5 size-4 rounded-full bg-primary p-0.5 text-white" />}
                  </button>
                ))}
              </div>
              <form className="mt-6 max-w-md" onSubmit={(e) => { e.preventDefault(); void save({ displayName: name }, 'Name saved'); }}>
                <label htmlFor="display-name" className="flex justify-between text-[13px] font-medium text-ink">Display name <span className="num text-muted-foreground">{name.length}/30</span></label>
                <div className="mt-1.5 flex gap-2">
                  <Input id="display-name" value={name} maxLength={30} onChange={(e) => setName(e.target.value)} className="h-10 bg-card" />
                  <Button type="submit" className="h-10 rounded-full px-5" disabled={name.trim() === (me.displayName || me.name)}>Save</Button>
                </div>
              </form>
              <div className="mt-5 max-w-md">
                <div className="text-[13px] font-medium text-ink">Email</div>
                <div className="mt-1.5 flex h-10 items-center justify-between rounded-lg border border-dashed px-3 text-sm">
                  <span className="truncate">{me.user}</span><span className="text-xs text-muted-foreground">Google account</span>
                </div>
              </div>
            </Row>

            <Row title="Preferences" note="Saved to your account, so they follow you to any device.">
              <Pref label="Theme">
                <Pills size="sm" label="Theme" value={theme} onChange={(t) => setTheme(t)}
                  options={[{ id: 'light', label: <><SunIcon className="size-3.5" />Light</> }, { id: 'dark', label: <><MoonIcon className="size-3.5" />Dark</> }, { id: 'system', label: <><MonitorIcon className="size-3.5" />System</> }]} />
              </Pref>
              <Pref label="Open after sign-in">
                <Pills size="sm" label="Start page" value={prefs.home ?? '/'} onChange={(h) => setPref({ home: h })} options={HOMES} />
              </Pref>
              <Pref label="Default index" note="The universe the board opens with.">
                <Select value={prefs.index ?? 'FNO'} onValueChange={(v) => setPref({ index: v })}>
                  <SelectTrigger className="h-9 w-56 bg-card" aria-label="Default index"><SelectValue /></SelectTrigger>
                  <SelectContent>{NSE_INDICES.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </Pref>
              <Pref label="Your broker" note="Stock pages get an “Open in …” button for it. Pages only; no orders.">
                <Select value={prefs.broker ?? 'none'} onValueChange={(v) => setPref({ broker: v === 'none' ? undefined : (v as Prefs['broker']) })}>
                  <SelectTrigger className="h-9 w-56 bg-card" aria-label="Your broker"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {BROKERS.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.app !== b.name ? ` (${b.app})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Pref>
              <Pref label="Risk per trade" note="The default for the position-size helper on stock pages.">
                <Pills size="sm" label="Risk per trade" value={String(prefs.riskPct ?? 1)} onChange={(r) => setPref({ riskPct: Number(r) })}
                  options={RISKS.map((r) => ({ id: r, label: `${r}%` }))} />
              </Pref>
            </Row>

            <Row title="Guardrails" note="Limits on your paper trading, so practice builds the habits real money needs. When one is reached, new paper trades pause until tomorrow.">
              <Pref label="Guardrails" note="Off lets you keep placing paper trades after a bad day.">
                <Pills size="sm" label="Guardrails" value={prefs.guardOff ? 'off' : 'on'} onChange={(v) => setPref({ guardOff: v === 'off' })} options={[{ id: 'on', label: 'On' }, { id: 'off', label: 'Off' }]} />
              </Pref>
              <Pref label="Pause after losing trades" note="Losing paper trades closed in one day.">
                <Pills size="sm" label="Pause after losing trades" value={String(prefs.maxLosses || 3)} onChange={(v) => setPref({ maxLosses: Number(v) })} options={['2', '3', '5'].map((n) => ({ id: n, label: n }))} />
              </Pref>
              <Pref label="Daily loss limit" note="Realised paper loss in a day, after costs. Empty: no limit.">
                <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); const v = Number(lossLimit.replace(/[^\d.]/g, '')) || 0; void setPref({ dayLossLimit: v || undefined }); }}>
                  <Input aria-label="Daily loss limit in rupees" inputMode="numeric" placeholder="₹ no limit" value={lossLimit} onChange={(e) => setLossLimit(e.target.value)} className="h-9 w-36 bg-card" />
                  <Button type="submit" size="sm" className="h-9 rounded-full px-4" disabled={(Number(lossLimit.replace(/[^\d.]/g, '')) || 0) === (prefs.dayLossLimit ?? 0)}>Save</Button>
                </form>
              </Pref>
            </Row>

            <Row title="Privacy" note="What MoneyHQ keeps, and where.">
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                Your trading capital (position size), chart layers and layout stay <b className="text-ink">in this browser only</b>; they are never sent to our servers.
                Your profile, preferences, shortlist and trading journal are stored with your account.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-full"
                  onClick={() => { try { LOCAL_KEYS.forEach((k) => localStorage.removeItem(k)); } catch { /* nothing stored */ } toast.success('Cleared from this browser'); }}>
                  Clear browser data
                </Button>
                {me.role !== 'admin' && (
                  <Button variant="outline" className="rounded-full text-bear hover:text-bear" onClick={() => setDeleting(true)}><Trash2Icon />Delete account</Button>
                )}
              </div>
            </Row>

            <Row title="Sign out" note="Ends your session on this device.">
              <Button variant="outline" className="rounded-full text-bear hover:text-bear" onClick={() => void signOut()}><LogOutIcon />Sign out</Button>
            </Row>
          </Card>
        )}
        {me?.role === 'admin' && <ReleaseSwitches />}
      </Page>
      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>Your profile, preferences, shortlist and trading journal are deleted permanently. You can sign up again later with Google.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(false)}>Cancel</Button>
            <Button variant="destructive" onClick={async () => {
              const r = await api('/v1/me', { method: 'DELETE' });
              if (r.ok) location.assign('/login'); else toast.error('Could not delete the account');
            }}>Delete account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
